import { ScanSearchIcon, TriangleAlertIcon } from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Empty, EmptyContent, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "@/components/ui/empty";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { PanelDeDudas } from "@/components/generacion/PanelDeDudas";
import { ProgresoDelAnalisis } from "@/components/generacion/ProgresoDelAnalisis";
import { TablaDeHechos } from "@/components/generacion/TablaDeHechos";
import { formatearFechaYHora } from "@/lib/fechas";
import {
  errorDelAnalisis,
  type CorridaDelAnalisis,
  type Duda,
  type FiltroDeDudas,
  type Hecho,
} from "@/lib/generacion";

export type PestanaDelAnalisis = "dudas" | "hechos";

export interface VistaDelAnalisis {
  pestana: PestanaDelAnalisis;
  filtro: FiltroDeDudas;
  dudaId: number | null;
}

interface AnalisisDelExpedienteProps {
  informeId: number;
  /** `undefined` mientras carga; `null` si nunca se analizó. */
  corrida: CorridaDelAnalisis | null | undefined;
  dudas: Duda[];
  hechos: Hecho[];
  vista: VistaDelAnalisis;
  onVista: (vista: VistaDelAnalisis) => void;
  onAnalizar: () => void;
  /** Abre la fase de valorización (la nota de las dudas que la pueden mover). */
  onIrALaValorizacion?: () => void;
  /** Hay un POST de analizar en vuelo. */
  lanzando: boolean;
}

/** La fase 1 entera, según en qué punto esté el análisis. */
export function AnalisisDelExpediente({
  informeId,
  corrida,
  dudas,
  hechos,
  vista,
  onVista,
  onAnalizar,
  onIrALaValorizacion,
  lanzando,
}: AnalisisDelExpedienteProps) {
  if (corrida === undefined) return <Skeleton className="h-48 w-full rounded-xl" />;

  if (corrida === null) {
    return (
      <Card className="py-2">
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <ScanSearchIcon aria-hidden />
            </EmptyMedia>
            <EmptyTitle>El expediente todavía no se analizó</EmptyTitle>
            <EmptyDescription>
              La IA lee los documentos, las notas de terreno y las pestañas, arma la hoja de hechos y levanta las
              dudas que tienes que responder. Puede tardar unos minutos y puedes cerrar la página mientras corre.
            </EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            <Button type="button" disabled={lanzando} onClick={onAnalizar}>
              Analizar el expediente
            </Button>
          </EmptyContent>
        </Empty>
      </Card>
    );
  }

  if (corrida.estado === "en_curso") return <ProgresoDelAnalisis corrida={corrida} />;

  const error = corrida.estado === "error" ? errorDelAnalisis(corrida.error ?? { codigo: "inesperado", mensaje: "" }) : null;
  // Un análisis que falló no borra lo que un análisis anterior ya levantó.
  const hayResultados = corrida.estado === "ok" || dudas.length > 0;

  return (
    <div className="flex flex-col gap-4">
      {error ? (
        <Alert variant="destructive">
          <TriangleAlertIcon aria-hidden />
          <AlertTitle className="line-clamp-none">{error.titulo}</AlertTitle>
          <AlertDescription>
            <p>{error.detalle}</p>
            {error.reintentable ? (
              <Button type="button" size="sm" variant="outline" className="mt-2" disabled={lanzando} onClick={onAnalizar}>
                Reintentar
              </Button>
            ) : null}
          </AlertDescription>
        </Alert>
      ) : null}

      {hayResultados ? (
        <Tabs
          variant="underline-ink"
          value={vista.pestana}
          onValueChange={(v) => onVista({ ...vista, pestana: v as PestanaDelAnalisis })}
        >
          <TabsList className="flex-wrap items-end gap-x-4 gap-y-1">
            <TabsTrigger value="dudas">
              Dudas <ContadorDeLaPestana n={dudas.length} />
            </TabsTrigger>
            <TabsTrigger value="hechos">
              Hoja de hechos <ContadorDeLaPestana n={hechos.length} />
            </TabsTrigger>
            <div className="ml-auto flex flex-wrap items-center gap-3 pb-2">
              <ProcedenciaDelAnalisis corrida={corrida} />
              {corrida.estado === "ok" ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-auto rounded-[0.625rem] border-input px-[0.6875rem] py-1.5 text-xs shadow-none"
                      disabled={lanzando}
                      onClick={onAnalizar}
                    >
                      Volver a analizar
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Las respuestas ya dadas se conservan.</TooltipContent>
                </Tooltip>
              ) : null}
            </div>
          </TabsList>

          <TabsContent value="dudas" className="pt-4">
            {dudas.length === 0 ? (
              <p className="text-meta text-ink-medio bg-card rounded-xl border-[1.5px] px-4 py-8 text-center">
                El análisis no levantó ninguna duda.
              </p>
            ) : (
              <PanelDeDudas
                informeId={informeId}
                dudas={dudas}
                hechos={hechos}
                filtro={vista.filtro}
                dudaId={vista.dudaId}
                onFiltro={(filtro) => onVista({ ...vista, filtro, dudaId: null })}
                onElegir={(dudaId) => onVista({ ...vista, dudaId })}
                onIrALaValorizacion={onIrALaValorizacion}
              />
            )}
          </TabsContent>
          <TabsContent value="hechos" className="pt-4">
            <TablaDeHechos informeId={informeId} hechos={hechos} />
          </TabsContent>
        </Tabs>
      ) : null}
    </div>
  );
}

/** El contador de una pestaña: aparte del rótulo, en mono y gris medio. */
function ContadorDeLaPestana({ n }: { n: number }) {
  return <span className="text-ink-medio font-mono text-[0.6875rem] font-bold">{n}</span>;
}

/** De cuándo es el análisis y qué leyó; lo que quedó sin leer se nombra. */
function ProcedenciaDelAnalisis({ corrida }: { corrida: CorridaDelAnalisis }) {
  const cuando = corrida.terminada_en ?? corrida.iniciada_en;
  const lectura = corrida.lectura;
  const aparte = [
    lectura && lectura.pendientes > 0
      ? `${lectura.pendientes} ${lectura.pendientes === 1 ? "documento todavía no se puede leer" : "documentos todavía no se pueden leer"}`
      : null,
    lectura && lectura.fuera_del_contexto.length > 0
      ? `Quedaron fuera del contexto: ${lectura.fuera_del_contexto.map((d) => d.nombre).join(", ")}`
      : null,
    lectura && lectura.sin_usar.length > 0
      ? `Sin usar porque otro documento tenía prioridad: ${lectura.sin_usar.map((d) => d.nombre).join(", ")}`
      : null,
  ].filter((x): x is string => x !== null);

  return (
    <div className="flex flex-col items-end gap-0.5 text-right">
      <span className="text-ink-medio text-[0.71875rem] font-semibold">
        {cuando ? `Análisis del ${formatearFechaYHora(cuando)} · ` : "Análisis · "}
        {corrida.contadores.documentos} documentos
      </span>
      {aparte.map((linea) => (
        <span key={linea} className="text-warning-texto max-w-prose text-[0.71875rem] font-semibold text-pretty">
          {linea}
        </span>
      ))}
    </div>
  );
}
